import React from 'react';
import PropTypes from 'prop-types';
import { useAppContext } from 'context/AppContext';
import strings from 'localization/strings';
import { makeStyles } from 'styles/util';
import { formatShortDate, parseIsoDate } from 'helpers/DateHelper';
import { startOfDay } from 'date-fns';
import { minimumDateString } from 'logic/bookingLogic';
import clsx from 'clsx';

import Box from '@material-ui/core/Box';
import MarginBox from 'common/MarginBox';
import Typography from '@material-ui/core/Typography';
import PriceSpecificationTable from './PriceSpecificationTable';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Amount from 'common/Amount';

const useStyles = makeStyles(() => ({
    accordion: {
        border: 'none',
        '&:before': {
            backgroundColor: 'transparent'
        }
    },
    accordionSummary: {
        paddingTop: 0,
        paddingBottom: 0
    },
    accordionDetails: {
        flexDirection: 'column',
        alignItems: 'flex-start'
    },
    amount: {
        fontWeight: 'bold'
    },
    noHorizontalPadding: {
        paddingLeft: 0,
        paddingRight: 0
    }
}));

const PriceSpecificationList = ({ booking, bookingPriceInfo, horizontalMargin = true }) => {
    const classes = useStyles();
    const { appContext } = useAppContext();

    const priceInfo = bookingPriceInfo ?? booking.priceInfo;

    const getAmount = amounts => amounts.totalAmount + amounts.totalVat + amounts.roundingCorrection;

    const getOneTimePriceLabel = amounts => getAmount(amounts) < 0
        ? strings.discountOneTime
        : strings.priceOneTime;

    const getPerMonthPriceLabel = (perMonthPriceInfo, showDate) => showDate
        ? strings.formatString(strings.pricePerMonthAfterPriceChangeAtX, formatShortDate(perMonthPriceInfo.date, appContext))
        : strings.pricePerMonth;

    const hasOneTimePurchaseItems = priceInfo.oneTime.purchaseItems?.length > 0;

    const renderAccordion = (summary, details) => (
        <Accordion variant="outlined" className={classes.accordion}>
            <AccordionSummary className={clsx(classes.accordionSummary, { [classes.noHorizontalPadding]: !horizontalMargin })} expandIcon={<ExpandMoreIcon />}>
                <Typography>
                    {summary}
                </Typography>
            </AccordionSummary>
            <AccordionDetails className={clsx(classes.accordionDetails, { [classes.noHorizontalPadding]: !horizontalMargin })}>
                {details}
            </AccordionDetails>
        </Accordion>
    );

    const renderSubscriptionBookingOneTimePurchaseItems = () =>
        renderAccordion(
            <>{getOneTimePriceLabel(priceInfo.oneTime.amounts)}: <span className={classes.amount}><Amount value={getAmount(priceInfo.oneTime.amounts)} currency={priceInfo.currency} displayPriceRounding /></span></>,
            <PriceSpecificationTable purchaseItems={priceInfo.oneTime.purchaseItems} currency={priceInfo.currency} showVat={priceInfo.hasVat} />
        );

    const renderSubscriptionBookingPerMonthPurchaseItems = (perMonthPriceInfo, showDate) =>
        renderAccordion(
            <>{getPerMonthPriceLabel(perMonthPriceInfo, showDate)}: <span className={classes.amount}><Amount value={getAmount(perMonthPriceInfo.amounts)} currency={priceInfo.currency} displayPriceRounding /></span></>,
            <PriceSpecificationTable purchaseItems={perMonthPriceInfo.purchaseItems} currency={priceInfo.currency} showVat={priceInfo.hasVat} />
        );

    const renderPeriodBookingPurchaseItems = () =>
        renderAccordion(
            <>{strings.price}: <span className={classes.amount}><Amount value={getAmount(priceInfo.wholePeriod.amounts)} currency={priceInfo.currency} displayPriceRounding /></span></>,
            <PriceSpecificationTable purchaseItems={priceInfo.wholePeriod.purchaseItems} currency={priceInfo.currency} showVat={priceInfo.hasVat} />
        );

    const renderAdditionalServicesWithUnknownPrice = () =>
        priceInfo.hasAdditionalServicesWithUnknownPrice && <MarginBox top={2}>{strings.hasAdditionalServicesWithUnknownPrice}</MarginBox>;

    const today = startOfDay(new Date());
    const perMonthPriceInfos = priceInfo.perMonth
        .filter(perMonthPriceInfo => parseIsoDate(perMonthPriceInfo.date) >= today)
        .filter((perMonthPriceInfo, i) => i === 0 || !perMonthPriceInfo.sameAmountAsPrevious);

    return (
        <>
            <Box>
                {
                    booking.subscriptionBooking &&
                    (
                        <>
                            {
                                perMonthPriceInfos.map((perMonthPriceInfo, i) => (
                                    <React.Fragment key={perMonthPriceInfo.date ?? minimumDateString}>
                                        {renderSubscriptionBookingPerMonthPurchaseItems(perMonthPriceInfo, i > 0)}
                                    </React.Fragment>
                                ))
                            }
                            {hasOneTimePurchaseItems && renderSubscriptionBookingOneTimePurchaseItems()}
                        </>
                    )
                }
                {
                    !booking.subscriptionBooking && renderPeriodBookingPurchaseItems()
                }
            </Box>
            {renderAdditionalServicesWithUnknownPrice()}
        </>
    );
};

PriceSpecificationList.propTypes = {
    booking: PropTypes.object.isRequired,
    bookingPriceInfo: PropTypes.object,
    horizontalMargin: PropTypes.bool
};

export default PriceSpecificationList;
